import { apiUrl } from "../config";
import { ClientRegistrationType } from "../redux/actions/clientRegistration";
import { AppSettings } from "../services/appSetting.service";
import { getSalesColumnSettings } from "./salesSettings";

const THERMAL_PRINT_URL =
    process.env.REACT_APP_THERMAL_PRINT_URL || "http://localhost:3005/print";

const THERMAL_LOGO_PUBLIC_PATH =
    process.env.REACT_APP_THERMAL_LOGO_PUBLIC_PATH || "/logo.png";

const THERMAL_QR_IMAGE_PUBLIC_PATH =
    process.env.REACT_APP_THERMAL_QR_IMAGE_PUBLIC_PATH || "/qr_code.jpeg";

const DEFAULT_MAX_THERMAL_IMAGE_URL_LEN = 2000;
const ADDRESS_MARGIN_DOTS = 18;
const PRODUCT_LIST_PAD_DOTS = 18;


const normalize = (s: string | undefined) => (s || "").trim().toLowerCase();

function pickPrinterSettingValue(
    settings: AppSettings[],
    key: string,
    branchId: number
): string | undefined {
    const groupOk = (s: AppSettings) => normalize(s.setting_group) === "printer settings";
    const keyOk = (s: AppSettings) => normalize(s.setting_key) === normalize(key);
    const candidates = settings.filter((s) => groupOk(s) && keyOk(s));
    const branchMatch = candidates.find((s) => (s.branch_id ?? 0) === branchId);
    if (branchMatch) return branchMatch.setting_value;
    const global = candidates.find((s) => (s.branch_id ?? 0) === 0);
    if (global) return global.setting_value;
    return candidates[0]?.setting_value;
}

function isTruthySetting(val: string | undefined): boolean {
    return normalize(val) === "true" || val === "1";
}

function resolveAssetUrl(pathOrUrl: string | undefined, base: string): string | null {
    if (!pathOrUrl) return null;
    const p = pathOrUrl.trim();
    if (!p) return null;
    if (/^https?:\/\//i.test(p)) return p;
    const b = (base || "").replace(/\/$/, "");
    if (!b) return p.startsWith("/") ? p : `/${p}`;
    return p.startsWith("/") ? `${b}${p}` : `${b}/${p}`;
}

export function getAbsolutePublicAssetUrl(relativePath: string): string {
    const path = relativePath.trim() || "/";
    const normalized = path.startsWith("/") ? path : `/${path}`;
    if (typeof window === "undefined" || !window.location?.origin) {
        return normalized;
    }
    const pub = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    return `${window.location.origin}${pub}${normalized}`;
}

function maxThermalImageUrlLength(): number {
    const n = Number(process.env.REACT_APP_THERMAL_IMAGE_URL_MAX_LEN);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_MAX_THERMAL_IMAGE_URL_LEN;
}

function isDataUrl(s: string): boolean {
    return /^data:/i.test(s.trim());
}

function pickThermalLogoUrl(
    showLogo: boolean,
    client: ClientRegistrationType | undefined,
    base: string
): string | null {
    if (!showLogo) return null;

    const publicUrl = getAbsolutePublicAssetUrl(THERMAL_LOGO_PUBLIC_PATH);
    if (process.env.REACT_APP_THERMAL_LOGO_USE_PUBLIC_ONLY === "true") {
        return publicUrl;
    }

    const maxLen = maxThermalImageUrlLength();
    const fromClient = resolveAssetUrl(client?.shopPhoto || client?.shopLogo, base);
    if (
        fromClient &&
        !isDataUrl(fromClient) &&
        /^https?:\/\//i.test(fromClient) &&
        fromClient.length <= maxLen
    ) {
        return fromClient;
    }

    return publicUrl;
}

function guessImageMimeFromUrl(url: string): string | undefined {
    try {
        const path = new URL(url).pathname.toLowerCase();
        if (path.endsWith(".png")) return "image/png";
        if (path.endsWith(".jpg") || path.endsWith(".jpeg")) return "image/jpeg";
        if (path.endsWith(".webp")) return "image/webp";
        if (path.endsWith(".gif")) return "image/gif";
    } catch {
        /* ignore */
    }
    return undefined;
}

function isSameOriginImageUrl(url: string): boolean {
    if (typeof window === "undefined") return false;
    try {
        return new URL(url).origin === window.location.origin;
    } catch {
        return false;
    }
}

function bufferLooksLikeRasterImage(buf: Uint8Array): boolean {
    if (buf.length < 12) return false;
    if (buf[0] === 0x3c || buf[0] === 0x7b || buf[0] === 0x5b) return false;
    const png =
        buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4e && buf[3] === 0x47;
    const jpeg = buf[0] === 0xff && buf[1] === 0xd8 && buf[2] === 0xff;
    const gif = buf[0] === 0x47 && buf[1] === 0x49 && buf[2] === 0x46;
    const webp =
        buf[0] === 0x52 &&
        buf[1] === 0x49 &&
        buf[2] === 0x46 &&
        buf[3] === 0x46 &&
        buf[8] === 0x57 &&
        buf[9] === 0x45 &&
        buf[10] === 0x42 &&
        buf[11] === 0x50;
    return png || jpeg || gif || webp;
}

async function normalizeThermalImageLine(
    line: Record<string, unknown>
): Promise<Record<string, unknown> | null> {
    const url = typeof line.url === "string" ? line.url : "";
    if (!url) return null;

    const extMime = guessImageMimeFromUrl(url);
    const out: Record<string, unknown> = { ...line };

    if (extMime) {
        out.mimeType = extMime;
    }

    if (!isSameOriginImageUrl(url)) {
        return out;
    }

    try {
        const res = await fetch(url, { method: "GET", mode: "cors", cache: "no-store" });
        if (!res.ok) return null;
        const blob = await res.blob();
        const buf = new Uint8Array(await blob.arrayBuffer());
        if (!bufferLooksLikeRasterImage(buf)) return null;
        const hdr = (res.headers.get("content-type") || "").split(";")[0].trim().toLowerCase();
        if (hdr && hdr.startsWith("image/")) {
            out.mimeType = hdr;
        } else if (!out.mimeType) {
            out.mimeType = guessImageMimeFromUrl(url) || "image/png";
        }
        return out;
    } catch {
        return null;
    }
}

async function sanitizeThermalPayloadImages(body: Record<string, unknown>): Promise<void> {
    const lines = body.lines;
    if (!Array.isArray(lines)) return;
    const next: Record<string, unknown>[] = [];
    for (const line of lines as Record<string, unknown>[]) {
        if (line.kind === "imageUrl" && typeof line.url === "string") {
            const normalized = await normalizeThermalImageLine(line);
            if (normalized) next.push(normalized);
            continue;
        }
        next.push(line);
    }
    body.lines = next;
}

function fitText(s: string, maxLen: number): string {
    const t = (s ?? "").toString();
    if (t.length <= maxLen) return t;
    return maxLen <= 1 ? "" : t.slice(0, maxLen - 1) + ".";
}

export function isThermalPrinterFromSettings(
    settings: AppSettings[],
    branchId: number
): boolean {
    return normalize(pickPrinterSettingValue(settings, "printer type", branchId)) === "thermal";
}

export interface ReceiptPrinterDisplayOptions {
    showLogo: boolean;
    showLogoText: boolean;
    showGstNo: boolean;
    showQr: boolean;
    footerMsg: string;
}

export function getReceiptPrinterDisplayOptions(
    settings: AppSettings[],
    branchId: number
): ReceiptPrinterDisplayOptions {
    return {
        showLogo: isTruthySetting(pickPrinterSettingValue(settings, "logo", branchId)),
        showLogoText: isTruthySetting(pickPrinterSettingValue(settings, "logo text", branchId)),
        showGstNo: isTruthySetting(pickPrinterSettingValue(settings, "gst no", branchId)),
        showQr: isTruthySetting(pickPrinterSettingValue(settings, "qr code", branchId)),
        footerMsg:pickPrinterSettingValue(settings, "footer message", branchId)?.trim() || "",
    };
}

/** Resolves logo URL for PDF/thermal (public fallback, length cap, etc.). */
export function getReceiptLogoImageUrl(
    settings: AppSettings[],
    branchId: number,
    client: ClientRegistrationType | undefined,
    apiBaseUrl: string
): string | null {
    const showLogo = isTruthySetting(pickPrinterSettingValue(settings, "logo", branchId));
    const base = apiBaseUrl || apiUrl || "";
    return pickThermalLogoUrl(showLogo, client, base);
}

export function getReceiptQrImageUrl(): string {
    const p = THERMAL_QR_IMAGE_PUBLIC_PATH.startsWith("/")
        ? THERMAL_QR_IMAGE_PUBLIC_PATH
        : `/${THERMAL_QR_IMAGE_PUBLIC_PATH}`;
    return getAbsolutePublicAssetUrl(p);
}

export interface BillPdfPrinterContext {
    settings: AppSettings[];
    branchId: number;
    paymentLabel: string;
    apiBaseUrl?: string;
}

export interface SalesPrintObject {
    billnumber: string;
    date: string;
    time: string;
    customer?: { name?: string; place?: string; conNo?: string; conNo2?: string; gstNo?: string };
    productitems?: Array<{
        name?: string;
        qty?: string;
        rate?: string;
        total?: string;
    }>;
    finaltotal?: string | number;
    paymentamount?: string | number;
    credit?: string | number;
    previousBal?: string | number;
}

export interface PostThermalSalesReceiptParams {
    printObj: SalesPrintObject;
    client?: ClientRegistrationType;
    settings: AppSettings[];
    branchId: number;
    paymentLabel: string;
    apiBaseUrl?: string;
}

function buildThermalSalesReceiptPayload(params: PostThermalSalesReceiptParams): Record<string, unknown> {
    const { printObj, client, settings, branchId, paymentLabel } = params;
    const base = params.apiBaseUrl ?? apiUrl ?? "";

    const printerInterface =
        process.env.REACT_APP_THERMAL_PRINTER_INTERFACE ||
        "winspool:RONGTA RP80 Printer";
    const printerType = process.env.REACT_APP_THERMAL_PRINTER_TYPE || "epson";
    const width = Number(process.env.REACT_APP_THERMAL_PRINT_WIDTH) || 48;

    const opts = getReceiptPrinterDisplayOptions(settings, branchId);
    const logoUrl = opts.showLogo ? pickThermalLogoUrl(true, client, base) : null;

    const shopName = client?.shopFullName || "";
    const addr1 = [client?.address1, client?.address2].filter(Boolean).join(", ");
    const addr2 = [client?.district, client?.pincode].filter(Boolean).join(" - ");

    const lines: Record<string, unknown>[] = [
        { kind: "lineSpacing", dots: 8 },
        { kind: "align", value: "center" },
    ];

    if (logoUrl) {
        const imageLine: Record<string, unknown> = {
            kind: "imageUrl",
            url: logoUrl,
            maxWidthPx: 260,
            maxHeightPx: 120,
            afterNewline: false,
        };
        const extMime = guessImageMimeFromUrl(logoUrl);
        if (extMime) {
            imageLine.mimeType = extMime;
        }
        lines.push(imageLine);
    }

    if (opts.showLogoText && shopName) {
        lines.push({
            kind: "text",
            value: shopName,
            size: "doubleWidth",
            bold: true,
        });
        lines.push({ kind: "newline" });
    }

    if (addr1) {
        lines.push({ kind: "lineSpacing", dots: ADDRESS_MARGIN_DOTS });
        lines.push({ kind: "text", value: addr1 });
        lines.push({ kind: "lineSpacing", dots: ADDRESS_MARGIN_DOTS });
    }
    if (addr2) {
        lines.push({ kind: "lineSpacing", dots: ADDRESS_MARGIN_DOTS });
        lines.push({ kind: "text", value: addr2 });
        lines.push({ kind: "lineSpacing", dots: ADDRESS_MARGIN_DOTS });
        lines.push({ kind: "newline" });
    }
    if (opts.showGstNo && client?.gstNo) {
        lines.push({ kind: "text", value: `GST No: ${client.gstNo}` });
    }

    lines.push({ kind: "newline" });
    lines.push({ kind: "text", value: "SALES TAX INVOICE", bold: true });
    lines.push({ kind: "newline" });
    lines.push({ kind: "align", value: "left" });

    const invRight = `${printObj.date} ${printObj.time}`.trim();
    const cusName = printObj.customer?.name ?? "";

    lines.push({
        kind: "leftRight",
        left: `Invoice: ${printObj.billnumber}`,
        right: invRight,
    });
    lines.push({ kind: "newline" });
    lines.push({
        kind: "leftRight",
        left: `Name: ${cusName}`,
        right: `Payment: ${paymentLabel}`,
    });
    lines.push({ kind: "line" });

    lines.push({ kind: "lineSpacing", dots: PRODUCT_LIST_PAD_DOTS });
    lines.push({
        kind: "table",
        columns: [
            { text: "No", cols: 3, bold: true },
            { text: "Particulars", cols: 25, bold: true },
            { text: "Qty", align: "RIGHT", cols: 4, bold: true },
            { text: "Rate", align: "RIGHT", cols: 8, bold: true },
            { text: "Total", align: "RIGHT", cols: 8, bold: true },
        ],
    });
    lines.push({ kind: "line" });
    lines.push({ kind: "lineSpacing", dots: PRODUCT_LIST_PAD_DOTS });

    const items = printObj.productitems || [];
    items.forEach((item, i) => {
        lines.push({
            kind: "table",
            columns: [
                { text: String(i + 1), cols: 3 },
                { text: fitText(item.name || "", 25), cols: 25 },
                { text: fitText(String(item.qty ?? ""), 4), align: "RIGHT", cols: 4 },
                { text: fitText(String(item.rate ?? ""), 8), align: "RIGHT", cols: 8 },
                { text: fitText(String(item.total ?? ""), 8), align: "RIGHT", cols: 8 },
            ],
        });
            lines.push({ kind: "newline" });

    });

    lines.push({ kind: "lineSpacing", dots: PRODUCT_LIST_PAD_DOTS });
    lines.push({ kind: "line" });

    const oldBalanceAmt = parseFloat(String(printObj.previousBal ?? 0)).toFixed(2);
    const billAmt = parseFloat(String(printObj.finaltotal ?? 0)).toFixed(2);
    const paidNum = parseFloat(String(printObj.paymentamount ?? ""));
    const billNum = parseFloat(String(printObj.finaltotal ?? 0));
    const paidAmt = (
        !Number.isNaN(paidNum) ? paidNum : billNum
    ).toFixed(2);
    const balanceAmt = parseFloat(String(printObj.credit ?? 0)).toFixed(2);
    const showPaymentParticulars = getSalesColumnSettings(settings, branchId).showPaymentParticulars;

    if (showPaymentParticulars && Number(oldBalanceAmt) > 0) {
        lines.push({ kind: "leftRight", left: "Old Balance", right: oldBalanceAmt });
    }
    if (billAmt) {
        lines.push({ kind: "leftRight", left: "Bill Amount", right: billAmt });
    }
    if (paidAmt) {
        lines.push({ kind: "leftRight", left: "Amount Paid", right: paidAmt });
    }
    if (balanceAmt) {
        lines.push({
            kind: "table",
            columns: [
                { text: "Balance", cols: 34, bold: true },
                { text: balanceAmt, align: "RIGHT", cols: 14, bold: true },
            ],
        });
    }

    lines.push({ kind: "newline" });
    lines.push({ kind: "align", value: "center" });

    if (opts.showQr) {
        const qrImageUrl = getReceiptQrImageUrl();
        const qrLine: Record<string, unknown> = {
            kind: "imageUrl",
            url: qrImageUrl,
            maxWidthPx: 180,
            maxHeightPx: 120,
        };
        const qrMime = guessImageMimeFromUrl(qrImageUrl);
        if (qrMime) {
            qrLine.mimeType = qrMime;
        }
        lines.push(qrLine);
    }

    if (opts.footerMsg) {
        lines.push({ kind: "newline" });
        lines.push({ kind: "text", value: opts.footerMsg });
    }

    return {
        printerInterface,
        printerType,
        width,
        initialFeedLines: 0,
        cutFeedLines: 0,
        lines,
        cut: true,
    };
}

export async function postThermalSalesReceiptIfNeeded(
    params: PostThermalSalesReceiptParams
): Promise<{ attempted: boolean; ok: boolean; error?: string }> {
    if (!isThermalPrinterFromSettings(params.settings, params.branchId)) {
        return { attempted: false, ok: true };
    }

    const body = buildThermalSalesReceiptPayload(params);
    await sanitizeThermalPayloadImages(body);

    try {
        const res = await fetch(THERMAL_PRINT_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const errText = await res.text().catch(() => "");
            return {
                attempted: true,
                ok: false,
                error: errText || `HTTP ${res.status}`,
            };
        }
        return { attempted: true, ok: true };
    } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Network error";
        return { attempted: true, ok: false, error: msg };
    }
}
